import React, { useEffect, useRef, useState } from 'react';
import {
  Loader2, TrendingUp, ShoppingBag, Undo2, Receipt, Truck,
  Megaphone, Wallet, AlertCircle, CheckCircle2, Sparkles, Banknote, Info, FileText,
  Building2, X, ChevronRight, Download, RefreshCw, Lock,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import toast from 'react-hot-toast';
import settlementReportService from '../../../services/settlementReportService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (val) => {
  const n = Number(val ?? 0);
  if (Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const fmtPct = (val) => {
  if (val == null) return '—';
  return `${Number(val).toFixed(1)}%`;
};

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ═════════════════════════════════════════════════════════════════════════════

export default function SettlementOverview({ sellerAccountId, from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cash position: bank credits (DB) + reserve (live SP-API, persisted snapshot)
  const [cashflow, setCashflow] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(false);
  const [refreshingReserve, setRefreshingReserve] = useState(false);

  // Money Flow drill-down: selected waterfall bucket + its transactions
  const [bucket, setBucket] = useState(null);     // { key, label } or null
  const [bucketTxs, setBucketTxs] = useState(null);
  const [bucketLoading, setBucketLoading] = useState(false);

  // PDF export
  const printRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (!sellerAccountId || !from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    settlementReportService.getOverviewByRange(sellerAccountId, from, to)
      .then((res) => { if (!cancelled) setData(res.data || res); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load overview');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sellerAccountId, from, to]);

  useEffect(() => {
    if (!sellerAccountId || !from || !to) return;
    let cancelled = false;
    setCashflowLoading(true);
    settlementReportService.getCashflow(sellerAccountId, from, to)
      .then((res) => { if (!cancelled) setCashflow(res.data || res); })
      .catch(() => { if (!cancelled) setCashflow(null); })
      .finally(() => { if (!cancelled) setCashflowLoading(false); });
    return () => { cancelled = true; };
  }, [sellerAccountId, from, to]);

  const handleRefreshReserve = async () => {
    if (refreshingReserve || !sellerAccountId || !from || !to) return;
    setRefreshingReserve(true);
    try {
      const res = await settlementReportService.getCashflow(sellerAccountId, from, to, true);
      setCashflow(res.data || res);
      toast.success('Reserve refreshed');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not refresh reserve');
    } finally {
      setRefreshingReserve(false);
    }
  };

  // Reset bucket selection whenever the date range or seller changes — stale detail otherwise.
  useEffect(() => { setBucket(null); setBucketTxs(null); }, [sellerAccountId, from, to]);

  useEffect(() => {
    if (!bucket || !sellerAccountId || !from || !to) { setBucketTxs(null); return; }
    let cancelled = false;
    setBucketLoading(true);
    settlementReportService.getBucketTransactions(sellerAccountId, from, to, bucket.key)
      .then((res) => { if (!cancelled) setBucketTxs(res.data || res); })
      .catch(() => { if (!cancelled) setBucketTxs([]); })
      .finally(() => { if (!cancelled) setBucketLoading(false); });
    return () => { cancelled = true; };
  }, [bucket, sellerAccountId, from, to]);

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-400">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle size={28} className="mx-auto text-rose-500 mb-2" />
        <p className="text-sm text-rose-700">{error}</p>
      </div>
    );
  }
  if (!data) return null;

  const { waterfall, kpis, depositAmount, computedDeposit,
          settlementsCount, currency, byTransactionType } = data;

  const sales = waterfall?.sales || {};
  const refunds = waterfall?.refunds || {};
  const fees = waterfall?.amazonFees || {};
  const tax = waterfall?.taxWithheld || {};
  const other = waterfall?.other || {};

  const customerPaid = Number(sales.customerPaid) || 0;

  /**
   * Snapshots the printable region to a canvas (via html2canvas) and slices it across A4 pages
   * (via jsPDF). Multi-page is the safer default — Overview content frequently overflows a single
   * sheet (hero + cash position + KPIs + waterfall + by-type + tax callout). The bucket drill-down
   * is intentionally NOT in the printable region — it's a transient inspection panel, not part of
   * the snapshot.
   */
  const handleDownloadPdf = async () => {
    if (!printRef.current || exportingPdf) return;
    setExportingPdf(true);
    const t = toast.loading('Generating PDF…');
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      // Slice the tall canvas image across multiple A4 pages by repositioning it negatively.
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
      }

      const fname = `reconciliation-overview_${formatDateForFilename(from)}_to_${formatDateForFilename(to)}.pdf`;
      pdf.save(fname);
      toast.success('PDF saved', { id: t });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('PDF export failed', { id: t });
    } finally {
      setExportingPdf(false);
    }
  };

  // Empty state
  if (settlementsCount === 0 && customerPaid === 0) {
    return (
      <div className="p-10 text-center">
        <FileText size={36} className="mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-semibold text-slate-700">No transactions in this date range</p>
        <p className="text-xs text-slate-500 mt-1">
          Try a different range or sync new settlements from Amazon.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 space-y-4">
      {/* ─── Toolbar (NOT in PDF) ──────────────────────────────── */}
      <div className="flex items-center justify-end -mb-2">
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={exportingPdf}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          title="Download Overview as PDF"
        >
          {exportingPdf ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
          {exportingPdf ? 'Generating…' : 'Download PDF'}
        </button>
      </div>

      {/* ─── Printable region ──────────────────────────────────── */}
      <div ref={printRef} className="space-y-4 bg-white">
      {/* ─── Hero ──────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#FFF4E6] to-[#FFE8C8] rounded-xl border border-[#FFD89E] p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#C45500] mb-1">
              Net for selected period
            </p>
            <p className="text-3xl sm:text-4xl font-black text-[#0F1111] tracking-tight">
              {fmt(computedDeposit)}
            </p>
            <p className="text-xs text-slate-600 mt-1.5">
              {fmtDate(from)} → {fmtDate(to)}
              {' · '}{currency || 'INR'}
              {settlementsCount > 0 && (
                <> · across <strong>{settlementsCount}</strong> settlement{settlementsCount === 1 ? '' : 's'}</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* ─── Cash Position: bank credits + reserve (independent questions) ─── */}
      <CashPosition
        cashflow={cashflow}
        loading={cashflowLoading}
        onRefreshReserve={handleRefreshReserve}
        refreshingReserve={refreshingReserve}
      />

      {/* ─── KPI Tiles ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiTile icon={ShoppingBag} label="Orders"     value={kpis?.orderCount ?? 0} />
        <KpiTile icon={Undo2}       label="Refund Rate" value={fmtPct(kpis?.refundRatePct)} sub={`${kpis?.refundCount ?? 0} refunded`} muted={Number(kpis?.refundRatePct) < 5} />
        <KpiTile icon={TrendingUp}  label="Avg Net / Order" value={fmt(kpis?.avgNetPerOrder)} />
        <KpiTile icon={Receipt}     label="Effective Fee Rate" value={fmtPct(kpis?.effectiveFeePct)} sub="of customer paid" warn={Number(kpis?.effectiveFeePct) > 25} />
      </div>

      {/* ─── Money Flow Waterfall (with click-to-drill detail panel) ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className={`bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5 ${
          bucket ? 'lg:col-span-2' : 'lg:col-span-5'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Money Flow</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {bucket ? 'Click another row to switch detail' : 'Click any row to drill into transactions'}
              </p>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Customer paid: {fmt(customerPaid)}
            </span>
          </div>

          <Waterfall
            customerPaid={customerPaid}
            sales={sales}
            promo={waterfall?.promotionalRebates}
            refunds={refunds} fees={fees} tax={tax} other={other}
            deposit={computedDeposit}
            selectedBucket={bucket?.key}
            onSelect={(key, label) => setBucket({ key, label })}
          />
        </div>

        {bucket && (
          <BucketDetail
            bucket={bucket}
            txs={bucketTxs}
            loading={bucketLoading}
            onClose={() => { setBucket(null); setBucketTxs(null); }}
          />
        )}
      </div>

      {/* ─── By Transaction Type ───────────────────────────────── */}
      {byTransactionType && byTransactionType.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900">By Transaction Type</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Breakdown by Amazon's transaction-type categories
              </p>
            </div>
          </div>
          <table className="min-w-full text-xs">
            <thead className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2 text-left font-semibold">Type</th>
                <th className="py-2 text-right font-semibold">Count</th>
                <th className="py-2 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {byTransactionType.map((row, i) => (
                <tr key={i}>
                  <td className="py-1.5 font-medium text-slate-700">{row.type}</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-500">{row.count}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${
                    Number(row.total) < 0 ? 'text-rose-700' : 'text-emerald-700'
                  }`}>{fmt(row.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-slate-200 font-bold">
              <tr>
                <td className="py-2 text-[10px] uppercase tracking-wider text-slate-500">Net</td>
                <td className="py-2 text-right text-[10px] uppercase tracking-wider text-slate-500">
                  {byTransactionType.reduce((s, r) => s + r.count, 0)}
                </td>
                <td className="py-2 text-right tabular-nums text-slate-900">
                  {fmt(byTransactionType.reduce((s, r) => s + Number(r.total), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ─── Tax Recovery Callout ──────────────────────────────── */}
      {Number(kpis?.claimableTaxTotal) > 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="bg-emerald-500 rounded-lg p-2 flex-shrink-0">
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-emerald-900">
                {fmt(kpis.claimableTaxTotal)} is reclaimable from the government
              </h3>
              <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
                Amazon withheld <strong>{fmt(Math.abs(Number(tax.tcsTotal) || 0))}</strong> as TCS
                (claim in your GSTR return) and{' '}
                <strong>{fmt(Math.abs(Number(tax.tdsSection194o) || 0))}</strong> as TDS under Section 194-O
                (claim in your income tax return). These are <strong>not</strong> Amazon fees — they're
                government withholdings.
              </p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <TaxChip label="TCS-CGST" value={tax.tcsCgst} />
                <TaxChip label="TCS-SGST" value={tax.tcsSgst} />
                <TaxChip label="TCS-IGST" value={tax.tcsIgst} />
                <TaxChip label="TDS 194-O" value={tax.tdsSection194o} />
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function formatDateForFilename(s) {
  if (!s) return 'unknown';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toISOString().slice(0, 10);
}

// ─── Cash Position ───────────────────────────────────────────────────────────
// Two cards answering distinct questions — they don't sum to anything by design.
//   Bank credits: cash that physically hit the seller's account during this window.
//   Reserve:      live snapshot of money Amazon is currently withholding, independent of range.

function CashPosition({ cashflow, loading, onRefreshReserve, refreshingReserve }) {
  if (loading && !cashflow) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 text-slate-400">
        <Loader2 size={16} className="animate-spin" />
        <span className="text-xs">Computing cash position…</span>
      </div>
    );
  }
  if (!cashflow) return null;

  const bankAmount = Number(cashflow.bankCredits?.amount) || 0;
  const bankCount = cashflow.bankCredits?.settlementCount || 0;
  const reserveAmount = Number(cashflow.reserve?.amount) || 0;
  const reserveStatus = cashflow.reserve?.status;
  const reserveAsOf = cashflow.reserve?.asOf;
  const reserveCount = cashflow.reserve?.transactionCount || 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <CashCard
        icon={Building2}
        tone="emerald"
        label="Bank credits this period"
        value={fmt(bankAmount)}
        hint={
          bankCount > 0
            ? `${bankCount} settlement${bankCount === 1 ? '' : 's'} deposited in this window`
            : 'No disbursements landed in this window'
        }
        tooltip="Cash deposited into your account during this window. May include earnings from prior periods, and may exclude earnings still being processed."
      />
      <CashCard
        icon={Lock}
        tone="amber"
        label="Held by Amazon (Reserve)"
        value={fmt(reserveAmount)}
        hint={
          reserveStatus === 'UNAVAILABLE'
            ? 'Live reserve unavailable — try refresh'
            : reserveAsOf
              ? `${reserveCount} pending txn${reserveCount === 1 ? '' : 's'} · as of ${fmtDateTime(reserveAsOf)}`
              : 'Click refresh to fetch'
        }
        tooltip="Amount Amazon is currently withholding for refunds, claims, and pending settlements. Live snapshot — not bound to the selected date range."
        sparkline={cashflow.reserve?.history}
        action={
          <button
            type="button"
            onClick={onRefreshReserve}
            disabled={refreshingReserve}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white/70 hover:bg-white border border-amber-300 text-amber-800 disabled:opacity-50"
            title="Refresh from Amazon (bypasses 6h cache)"
          >
            <RefreshCw size={10} className={refreshingReserve ? 'animate-spin' : ''} />
            {refreshingReserve ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />
    </div>
  );
}

function CashCard({ icon: Icon, tone, label, value, hint, tooltip, action, sparkline }) {
  const palette = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
  }[tone] || 'bg-white border-slate-200 text-slate-800';
  const iconCls = {
    emerald: 'text-emerald-600',
    amber:   'text-amber-600',
  }[tone] || 'text-slate-500';
  const strokeCls = {
    emerald: 'stroke-emerald-600',
    amber:   'stroke-amber-600',
  }[tone] || 'stroke-slate-500';
  return (
    <div className={`rounded-xl border p-4 ${palette}`} title={tooltip || undefined}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-80">
          <Icon size={12} className={iconCls} />
          {label}
        </div>
        {action}
      </div>
      <div className="flex items-end justify-between gap-3 mt-1.5">
        <p className="text-xl font-black tabular-nums">{value}</p>
        {sparkline && sparkline.length >= 2 && (
          <Sparkline points={sparkline} strokeCls={strokeCls} />
        )}
      </div>
      {hint && <p className="text-[10px] mt-1 opacity-70">{hint}</p>}
    </div>
  );
}

function Sparkline({ points, strokeCls }) {
  const W = 80;
  const H = 24;
  const PAD = 2;
  const values = points.map(p => Number(p.amount) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = (W - PAD * 2) / (values.length - 1);
  const path = values
    .map((v, i) => {
      const x = PAD + i * stepX;
      const y = PAD + (H - PAD * 2) * (1 - (v - min) / range);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const last = values[values.length - 1];
  const lastX = PAD + (values.length - 1) * stepX;
  const lastY = PAD + (H - PAD * 2) * (1 - (last - min) / range);
  const tooltip = `${points.length} points · min ${fmt(min)} · max ${fmt(max)}`;
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="flex-shrink-0 opacity-80"
      title={tooltip}
    >
      <title>{tooltip}</title>
      <path d={path} fill="none" className={strokeCls} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="1.8" className={`${strokeCls} fill-current`} />
    </svg>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function KpiTile({ icon: Icon, label, value, sub, warn, muted }) {
  const tone = warn
    ? 'bg-rose-50 border-rose-200 text-rose-700'
    : muted
      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
      : 'bg-white border-slate-200 text-slate-700';
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-75">
        <Icon size={11} /> {label}
      </div>
      <p className="text-lg font-bold mt-1.5 tabular-nums">{value ?? '—'}</p>
      {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

function TaxChip({ label, value }) {
  const n = Number(value) || 0;
  return (
    <div className="bg-white/60 rounded-lg border border-emerald-200 px-2.5 py-1.5">
      <p className="text-[9px] font-semibold uppercase tracking-wider text-emerald-700">{label}</p>
      <p className="text-xs font-bold text-emerald-900 tabular-nums">{fmt(Math.abs(n))}</p>
    </div>
  );
}

function Waterfall({
  customerPaid, sales, promo, refunds, fees, tax, other, deposit,
  selectedBucket, onSelect,
}) {
  const max = Math.max(Math.abs(customerPaid), 1);

  const rows = [
    {
      bucket: 'customer-paid',
      label: 'Customer paid', icon: ShoppingBag, value: customerPaid, kind: 'in', highlight: true,
      detail: `${fmt(sales.productSales)} sales + ${fmt(sales.salesTaxCollected)} GST collected`,
    },
    Number(promo) !== 0 && {
      bucket: 'promo-rebates',
      label: 'Promotional rebates', icon: Sparkles, value: promo, kind: 'out',
      detail: 'Discounts you offered',
    },
    Number(refunds.netRefund) !== 0 && {
      bucket: 'refunds',
      label: 'Refunds', icon: Undo2, value: refunds.netRefund, kind: 'out',
      detail: `${refunds.count} order${refunds.count === 1 ? '' : 's'} refunded`,
    },
    Number(fees.sellingFees) !== 0 && {
      bucket: 'selling-fees',
      label: 'Selling fees (commission)', icon: Receipt, value: fees.sellingFees, kind: 'out',
      detail: 'Amazon referral + closing fees',
    },
    Number(fees.fbaFees) !== 0 && {
      bucket: 'fba-fees',
      label: 'FBA fees', icon: Wallet, value: fees.fbaFees, kind: 'out',
      detail: 'Pick & pack + weight handling (incl. GST)',
    },
    Number(fees.easyShipFees) !== 0 && {
      bucket: 'easy-ship',
      label: 'Easy Ship charges', icon: Truck, value: fees.easyShipFees, kind: 'out',
      detail: 'Shipping fees for self-fulfilled orders',
    },
    Number(fees.advertising) !== 0 && {
      bucket: 'advertising',
      label: 'Advertising', icon: Megaphone, value: fees.advertising, kind: 'out',
      detail: 'Sponsored ads spend',
    },
    Number(fees.subscription) !== 0 && {
      bucket: 'subscription',
      label: 'Subscription', icon: Receipt, value: fees.subscription, kind: 'out',
      detail: 'Pro Seller subscription',
    },
    Number(fees.otherServiceFees) !== 0 && {
      bucket: 'other-service-fees',
      label: 'Other service fees', icon: Receipt, value: fees.otherServiceFees, kind: 'out',
    },
    Number(tax.tcsTotal) !== 0 && {
      bucket: 'tcs',
      label: 'TCS withheld', icon: Banknote, value: tax.tcsTotal, kind: 'tax',
      detail: 'CGST + SGST + IGST — claim in GSTR',
    },
    Number(tax.tdsSection194o) !== 0 && {
      bucket: 'tds',
      label: 'TDS Section 194-O', icon: Banknote, value: tax.tdsSection194o, kind: 'tax',
      detail: 'Claim in income tax return',
    },
    Number(other.fbaFeeRefunds) !== 0 && {
      bucket: 'fba-fee-refunds',
      label: 'FBA fee refunds', icon: Undo2, value: other.fbaFeeRefunds, kind: 'in',
      detail: 'Amazon returned some FBA fees',
    },
    Number(other.adjustments) !== 0 && {
      bucket: 'adjustments',
      label: 'Adjustments', icon: Info, value: other.adjustments,
      kind: Number(other.adjustments) >= 0 ? 'in' : 'out',
      detail: 'SAFE-T / cancellation credits / misc',
    },
    Number(other.debtRecovery) !== 0 && {
      bucket: 'debt-recovery',
      label: 'Debt recovery', icon: Info, value: other.debtRecovery, kind: 'out',
      detail: "Amazon clawing back unpaid balance",
    },
    Number(other.orderOtherAmount) !== 0 && {
      bucket: 'order-other',
      label: 'Other on orders', icon: Info, value: other.orderOtherAmount,
      kind: Number(other.orderOtherAmount) >= 0 ? 'in' : 'out',
      detail: 'Misc charges/credits not in standard buckets',
    },
  ].filter(Boolean);

  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <WaterfallRow
          key={r.bucket} {...r} maxValue={max}
          selected={selectedBucket === r.bucket}
          onClick={() => onSelect(r.bucket, r.label)}
        />
      ))}

      <div className="pt-3 mt-2 border-t-2 border-dashed border-slate-200">
        <div className="flex items-center justify-between gap-3 py-2 px-3 bg-emerald-50 border-2 border-emerald-300 rounded-lg">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <span className="text-sm font-bold text-emerald-900">Net for the period</span>
          </div>
          <span className="text-base font-bold text-emerald-900 tabular-nums">{fmt(deposit)}</span>
        </div>
      </div>
    </div>
  );
}

function WaterfallRow({
  label, icon: Icon, value, kind, detail, highlight, maxValue, selected, onClick,
}) {
  const n = Number(value) || 0;
  const widthPct = Math.min(100, (Math.abs(n) / maxValue) * 100);

  const tone = {
    in:    { bar: 'bg-emerald-400/70', text: 'text-emerald-700' },
    out:   { bar: 'bg-rose-300',       text: 'text-rose-700' },
    tax:   { bar: 'bg-amber-300',      text: 'text-amber-700' },
  }[kind] || { bar: 'bg-slate-300', text: 'text-slate-600' };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-md px-1 py-0.5 transition-colors ${
        selected
          ? 'bg-[#FFF4E6] ring-2 ring-[#f08804]'
          : 'hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 w-44 sm:w-56 flex-shrink-0">
          <Icon size={12} className={selected ? 'text-[#C45500]' : 'text-slate-400'} />
          <span className={`text-xs ${highlight ? 'font-bold' : 'font-medium'} text-slate-700 truncate`} title={label}>
            {label}
          </span>
        </div>

        <div className="flex-1 relative h-6 bg-slate-50/50 rounded overflow-hidden">
          <div className={`absolute inset-y-0 left-0 ${tone.bar} transition-all duration-500`}
            style={{ width: `${widthPct}%` }} />
          {detail && (
            <span className="absolute inset-y-0 left-2 flex items-center text-[10px] text-slate-500 italic pointer-events-none">
              {detail}
            </span>
          )}
        </div>

        <span className={`text-xs font-bold tabular-nums ${tone.text} w-24 sm:w-28 text-right flex-shrink-0`}>
          {fmt(n)}
        </span>
        <ChevronRight size={12} className={`flex-shrink-0 ${selected ? 'text-[#C45500]' : 'text-slate-300'}`} />
      </div>
    </button>
  );
}

// ─── Bucket Detail Panel ─────────────────────────────────────────────────────
//
// Shows the underlying transactions for the selected Money Flow row, in the same column layout
// as the Amazon settlement reconciliation sheet. Horizontally scrollable so all 27 columns are
// reachable; sticky first columns (date, type, order id) stay visible while scrolling.

function BucketDetail({ bucket, txs, loading, onClose }) {
  const isLoading = loading && !txs;
  const list = txs || [];
  const total = list.reduce((s, t) => s + (Number(t.totalAmount) || 0), 0);

  // Mirror the bottom horizontal scrollbar at the top so it's reachable without scrolling down.
  const topScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    if (!bodyScrollRef.current) return;
    const tableEl = bodyScrollRef.current.querySelector('table');
    if (tableEl) setTableWidth(tableEl.scrollWidth);
  }, [list.length, isLoading]);

  const syncFromTop = (e) => {
    if (bodyScrollRef.current && bodyScrollRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
      bodyScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };
  const syncFromBody = (e) => {
    if (topScrollRef.current && topScrollRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
      topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 lg:col-span-3 flex flex-col min-h-0">
      <div className="flex items-start justify-between gap-2 p-4 border-b border-slate-200">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#C45500]">
            Drill-down · {bucket.label}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {isLoading
              ? 'Loading transactions…'
              : `${list.length} row${list.length === 1 ? '' : 's'} · total ${fmt(total)}`}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 hover:bg-slate-100 text-slate-500 flex-shrink-0"
          aria-label="Close detail"
        >
          <X size={14} />
        </button>
      </div>

      {isLoading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 size={18} className="animate-spin" />
        </div>
      ) : list.length === 0 ? (
        <div className="p-8 text-center text-xs text-slate-500">
          No transactions in this bucket for the selected period.
        </div>
      ) : (
        <>
          {/* Mirrored top scrollbar — drag this OR the bottom one, both stay in sync. */}
          <div
            ref={topScrollRef}
            onScroll={syncFromTop}
            className="overflow-x-scroll overflow-y-hidden flex-shrink-0 bg-slate-50 border-b border-slate-200"
            style={{ height: 18 }}
          >
            <div style={{ width: tableWidth, height: 1 }} />
          </div>
          <div
            ref={bodyScrollRef}
            onScroll={syncFromBody}
            className="overflow-x-auto overflow-y-auto max-h-[640px]"
          >
          <table className="text-[11px] w-max">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <Th sticky="left-0" w="w-32">Date / time</Th>
                <Th w="w-28">Settlement</Th>
                <Th w="w-24">Type</Th>
                <Th w="w-40">Order ID</Th>
                <Th w="w-32">SKU</Th>
                <Th w="w-48">Description</Th>
                <Th w="w-12" right>Qty</Th>
                <Th w="w-32">Account type</Th>
                <Th w="w-20">Fulfillment</Th>
                <Th w="w-28">City</Th>
                <Th w="w-24">State</Th>
                <Th w="w-20">Postal</Th>
                <Th w="w-24" right>Product sales</Th>
                <Th w="w-24" right>Shipping</Th>
                <Th w="w-20" right>Gift wrap</Th>
                <Th w="w-24" right>Promo rebate</Th>
                <Th w="w-24" right>GST liable</Th>
                <Th w="w-20" right>TCS-CGST</Th>
                <Th w="w-20" right>TCS-SGST</Th>
                <Th w="w-20" right>TCS-IGST</Th>
                <Th w="w-24" right>TDS 194-O</Th>
                <Th w="w-24" right>Selling fees</Th>
                <Th w="w-24" right>FBA fees</Th>
                <Th w="w-28" right>Other tx fees</Th>
                <Th w="w-20" right>Other</Th>
                <Th w="w-24" right sticky="right-0">Total</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <Td sticky="left-0" mono>{fmtDateTime(t.transactionDatetime)}</Td>
                  <Td mono className="text-slate-500">{t.settlementId || '—'}</Td>
                  <Td>{t.transactionType || '—'}</Td>
                  <Td mono>{t.amazonOrderId || '—'}</Td>
                  <Td mono>{t.sku || '—'}</Td>
                  <Td className="text-slate-600 truncate max-w-[12rem]" title={t.description}>{t.description || '—'}</Td>
                  <Td right mono>{t.quantity ?? '—'}</Td>
                  <Td className="text-slate-500 truncate max-w-[8rem]" title={t.accountType}>{t.accountType || '—'}</Td>
                  <Td>{t.fulfillment || '—'}</Td>
                  <Td>{t.shipCity || '—'}</Td>
                  <Td>{t.shipState || '—'}</Td>
                  <Td>{t.shipPostal || '—'}</Td>
                  <Money v={t.productSales} />
                  <Money v={t.shippingCredits} />
                  <Money v={t.giftWrapCredits} />
                  <Money v={t.promotionalRebates} />
                  <Money v={t.salesTaxLiableGst} />
                  <Money v={t.tcsCgst} />
                  <Money v={t.tcsSgst} />
                  <Money v={t.tcsIgst} />
                  <Money v={t.tdsSection194o} />
                  <Money v={t.sellingFees} />
                  <Money v={t.fbaFees} />
                  <Money v={t.otherTransactionFees} />
                  <Money v={t.otherAmount} />
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold sticky right-0 bg-white border-l border-slate-100">
                    <span className={Number(t.totalAmount) < 0 ? 'text-rose-700' : 'text-emerald-700'}>
                      {fmt(t.totalAmount)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-white border-t-2 border-slate-200 font-bold">
              <tr>
                <td className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 sticky left-0 bg-white" colSpan={25}>
                  Total ({list.length} row{list.length === 1 ? '' : 's'})
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums sticky right-0 bg-white">
                  <span className={total < 0 ? 'text-rose-700' : 'text-emerald-700'}>
                    {fmt(total)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children, w, right, sticky }) {
  return (
    <th className={[
      'px-2 py-2 font-semibold whitespace-nowrap',
      right ? 'text-right' : 'text-left',
      w || '',
      sticky ? `sticky ${sticky} bg-slate-50 z-10` : '',
    ].filter(Boolean).join(' ')}>
      {children}
    </th>
  );
}

function Td({ children, right, mono, sticky, className }) {
  return (
    <td className={[
      'px-2 py-1.5 whitespace-nowrap',
      right ? 'text-right' : 'text-left',
      mono ? 'tabular-nums' : '',
      sticky ? `sticky ${sticky} bg-white border-r border-slate-100` : '',
      className || '',
    ].filter(Boolean).join(' ')}>
      {children}
    </td>
  );
}

function Money({ v }) {
  const n = Number(v) || 0;
  if (!n) return <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">—</td>;
  return (
    <td className={`px-2 py-1.5 text-right tabular-nums ${n < 0 ? 'text-rose-700' : 'text-slate-700'}`}>
      {fmt(n)}
    </td>
  );
}

function fmtDateTime(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}
